import { useEffect } from "react";
import { useParams } from "react-router";
import { CalendarClock } from "lucide-react";
import { useCustomerLinkAdapter } from "@shared-core/data/CustomerLinkAdapterContext";
import type { ProofPageView } from "@shared-core/data/customer-link-adapter";
import {
  LinkErrorCard,
  LinkLayout,
  LinkLoadingCard,
  LinkNotFoundCard,
  OrderLink,
  ProofEndedCard,
} from "./LinkLayout";
import { ProofDecisionForm } from "./ProofDecisionForm";
import { ProofMadeFrom, ProofOutcome, ProofPdf } from "./ProofReview";
import { useLinkRead } from "./useLinkRead";

// One proof by its own emailed link (order to invoice doc 66 §6.4): the
// proof's PDF, what it was made from and its check results, and the decision
// (the order page's own form: approve with the worth-knowing points ticked,
// or ask for changes with a reason and words). The token is for this proof
// and nothing else, so there is no navigation to the rest of the order: the
// proof email links to the proof only, and the order shows as its number in
// plain text (Ben, 7 Oct). Once decided the same link reads the proof
// read-only; a proof a newer one replaced answers with the way to the order.
// Built without a mock-up: it composes the order page's proof review and the
// link chrome. Every sentence is the read's; the page only places it.

function ProofLinkBody({
  page,
  reload,
}: {
  page: ProofPageView;
  reload: () => Promise<void>;
}) {
  const adapter = useCustomerLinkAdapter();
  const { proof } = page;
  const heading = `${page.item_title}${page.display ? ` · ${page.display}` : ""} · ${proof.label}`;

  useEffect(() => {
    if (proof.opened_url && !proof.is_opened) {
      void adapter.artwork.markProofOpened(proof.opened_url).then(reload);
    }
    // Marking a proof opened is a side effect of first showing it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proof.id]);

  const decideUrl = proof.decide_url;
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="font-mono text-xs text-muted-foreground">
          Order {page.order_no}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {heading}
        </h1>
        <p className="text-sm text-muted-foreground">{proof.status_line}</p>
        {page.approve_by_sentence && (
          <p className="inline-flex items-center gap-2 rounded-md bg-muted/60 px-2.5 py-1.5 text-sm font-medium">
            <CalendarClock className="size-4" />
            {page.approve_by_sentence}
          </p>
        )}
      </div>

      <ProofPdf
        proof={proof}
        title={heading}
        source={{
          fetchFile: adapter.fetchFile,
        }}
      />

      <ProofMadeFrom proof={proof} />

      {decideUrl && page.wording_version !== null ? (
        <ProofDecisionForm
          idPrefix={`proof-${proof.id}`}
          approveConsequence="Approving means we print it exactly as shown here. Each item goes to print once all its proofs are approved."
          findingsToAccept={proof.findings.filter(
            (f) => f.tier === "worth_knowing",
          )}
          reasonOptions={proof.reason_options}
          wordingVersion={page.wording_version}
          onSubmit={async (decision) => {
            await adapter.artwork.decideProof(decideUrl, decision);
            await reload();
          }}
        />
      ) : (
        <ProofOutcome proof={proof} />
      )}

      {page.order_page_url && (
        <p className="text-sm">
          <OrderLink
            url={page.order_page_url}
            className="text-primary hover:underline"
          >
            Open your order
          </OrderLink>
        </p>
      )}
    </div>
  );
}

/** `/proof/:token`: one proof by its emailed link, no sign-in. */
export function ProofLinkPage() {
  const { token = "" } = useParams<{ token: string }>();
  const adapter = useCustomerLinkAdapter();
  const { state, reload } = useLinkRead(
    token,
    () => adapter.getProof(token),
    "We couldn't load this proof.",
  );

  return (
    <LinkLayout signedInWhere="Proofs">
      {state.kind === "loading" ? (
        <LinkLoadingCard what="proof" />
      ) : state.kind === "error" ? (
        <LinkErrorCard
          what="proof"
          message={state.message}
          onRetry={() => void reload()}
        />
      ) : state.kind === "gone" ? (
        <ProofEndedCard sentence={state.sentence} orderPageUrl={null} />
      ) : state.kind === "replaced" ? (
        <ProofEndedCard
          sentence={state.sentence}
          orderPageUrl={state.order_page_url}
        />
      ) : state.kind === "not_found" ? (
        <LinkNotFoundCard />
      ) : (
        <ProofLinkBody page={state.view} reload={reload} />
      )}
    </LinkLayout>
  );
}
